import React, { useCallback, useMemo } from 'react'
import { Table, Select, Typography } from '@surecompanies/core_components'
import type { ColumnsType } from 'antd/es/table'
import { DownOutlined, RightOutlined } from '@ant-design/icons'
import styled from 'styled-components'
import { useSelector } from 'react-redux'
import { selectors as companySelectors } from 'store/modules/companies'
import type {
  GetClientMetricsResponseDto,
  EmployeeBenefitMetricDto,
} from '../../../api/dto/get-payment-assignment-metrics.dto'
import {
  PAYMENT_ASSIGNMENT_STATUS_LABELS,
  PaymentAssignmentStatusId,
} from '../Utils/types'
import { DEFAULT_STATUSES } from './payment-assignment-metrics.config'

const { Text } = Typography

const fmt = (n: number) => n.toLocaleString()

interface EmployeeRow {
  key: string
  label: string
  [status: string]: string | number | boolean | undefined
}

interface CompanyPerspectiveMetricsProps {
  data: GetClientMetricsResponseDto | undefined
  loading?: boolean
  selectedCompanyIds: string[]
  onCompanyIdsChange: (ids: string[]) => void
  expanded: boolean
  onExpandedChange: (expanded: boolean) => void
}

interface CompanyRow {
  key: string
  label: string
  isGrandTotal?: boolean
  children?: EmployeeRow[]
  [status: string]: string | number | boolean | EmployeeRow[] | undefined
}

const SectionCard = styled.div`
  margin-bottom: 24px;
  background: #fff;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  overflow: hidden;
`

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  cursor: pointer;
  user-select: none;

  &:hover {
    background-color: #fafafa;
  }
`

const ExpandLink = styled.span`
  color: #1890ff;
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 4px;
`

const FilterRow = styled.div`
  padding: 8px 24px 16px;
`

function collectStatuses(data: GetClientMetricsResponseDto): string[] {
  const seen = new Set<string>(DEFAULT_STATUSES)
  const extra: string[] = []
  for (const employees of Object.values(data)) {
    for (const emp of employees) {
      for (const s of Object.keys(emp.status)) {
        if (!seen.has(s)) {
          seen.add(s)
          extra.push(s)
        }
      }
    }
  }
  return [...DEFAULT_STATUSES, ...extra]
}

function buildColumns(
  statuses: string[]
): ColumnsType<CompanyRow | EmployeeRow> {
  const cols: ColumnsType<CompanyRow | EmployeeRow> = [
    {
      title: 'Company / Employee',
      dataIndex: 'label',
      key: 'label',
      fixed: 'left' as const,
      width: 240,
      render: (text: string, record: CompanyRow | EmployeeRow) =>
        (record as CompanyRow).isGrandTotal ? <Text strong>{text}</Text> : text,
    },
  ]

  for (const status of statuses) {
    const label =
      PAYMENT_ASSIGNMENT_STATUS_LABELS[status as PaymentAssignmentStatusId] ??
      status.replace(/_/g, ' ')
    cols.push({
      title: label.toUpperCase(),
      dataIndex: status,
      key: status,
      align: 'center' as const,
      width: 170,
      render: (value: number | undefined, record: CompanyRow | EmployeeRow) => {
        const num = value ?? 0
        return (record as CompanyRow).isGrandTotal ? (
          <Text strong>{fmt(num)}</Text>
        ) : (
          fmt(num)
        )
      },
    })
  }

  cols.push({
    title: 'TOTAL',
    key: '__total__',
    fixed: 'right' as const,
    align: 'center' as const,
    width: 120,
    render: (_: unknown, record: CompanyRow | EmployeeRow) => {
      const total = statuses.reduce(
        (sum, s) => sum + (Number(record[s]) || 0),
        0
      )
      return (record as CompanyRow).isGrandTotal ? (
        <Text strong>{fmt(total)}</Text>
      ) : (
        fmt(total)
      )
    },
  })

  return cols
}

function buildCompanyRows(
  data: GetClientMetricsResponseDto,
  companyNameMap: Record<string, string>,
  statuses: string[]
): CompanyRow[] {
  const rows: CompanyRow[] = []
  const grandTotals: Record<string, number> = {}

  for (const [companyId, employees] of Object.entries(data)) {
    const companyName = companyNameMap[companyId] ?? companyId

    const companyCounts: Record<string, number> = {}
    const childRows: EmployeeRow[] = employees.map(
      (emp: EmployeeBenefitMetricDto) => {
        const row: EmployeeRow = {
          key: emp.benefitId,
          label: `${emp.insuredPersonFirstName} ${emp.insuredPersonLastName}`,
        }
        for (const s of statuses) {
          const val = emp.status[s] ?? 0
          row[s] = val
          companyCounts[s] = (companyCounts[s] ?? 0) + val
        }
        return row
      }
    )

    const companyRow: CompanyRow = {
      key: companyId,
      label: companyName,
      children: childRows,
    }
    for (const s of statuses) {
      companyRow[s] = companyCounts[s] ?? 0
      grandTotals[s] = (grandTotals[s] ?? 0) + (companyCounts[s] ?? 0)
    }
    rows.push(companyRow)
  }

  const grandTotal: CompanyRow = {
    key: 'grand-total',
    label: 'Grand Total',
    isGrandTotal: true,
  }
  for (const s of statuses) {
    grandTotal[s] = grandTotals[s] ?? 0
  }
  rows.push(grandTotal)

  return rows
}

export default function CompanyPerspectiveMetrics({
  data,
  loading,
  selectedCompanyIds,
  onCompanyIdsChange,
  expanded,
  onExpandedChange,
}: CompanyPerspectiveMetricsProps) {
  const companies = useSelector(companySelectors.companyCollectionSelector) as {
    id: string
    companyName: string
  }[]

  const companyOptions = useMemo(
    () =>
      [...(companies ?? [])]
        .map((c) => ({ label: c.companyName, value: c.id }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [companies]
  )

  const companyNameMap = useMemo(() => {
    const map: Record<string, string> = {}
    for (const c of companies ?? []) {
      map[c.id] = c.companyName
    }
    return map
  }, [companies])

  const statuses = useMemo(
    () => (data ? collectStatuses(data) : [...DEFAULT_STATUSES]),
    [data]
  )

  const columns = useMemo(() => buildColumns(statuses), [statuses])

  const rows = useMemo(
    () => (data ? buildCompanyRows(data, companyNameMap, statuses) : []),
    [data, companyNameMap, statuses]
  )

  const handleCompanyChange = useCallback(
    (values: string[]) => {
      onCompanyIdsChange(values)
    },
    [onCompanyIdsChange]
  )

  const selectedCount = selectedCompanyIds.length

  return (
    <SectionCard>
      <SectionHeader onClick={() => onExpandedChange(!expanded)}>
        <Text strong style={{ fontSize: 15 }}>
          Company Perspective
        </Text>
        <ExpandLink>
          {expanded ? (
            <>
              <DownOutlined /> Collapse
            </>
          ) : (
            <>
              <RightOutlined /> Expand
            </>
          )}
        </ExpandLink>
      </SectionHeader>

      {expanded && (
        <>
          <FilterRow>
            <Text style={{ display: 'block', marginBottom: 4 }}>
              Select Companies to Display:
            </Text>
            <Select
              mode="multiple"
              style={{ width: '100%' }}
              placeholder="Select companies..."
              value={selectedCompanyIds}
              onChange={handleCompanyChange}
              options={companyOptions}
              maxTagCount="responsive"
              maxTagPlaceholder={() =>
                `${selectedCount} compan${selectedCount === 1 ? 'y' : 'ies'} selected`
              }
              allowClear
              showSearch
              filterOption={(input, option) =>
                String(option?.label ?? '')
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            />
          </FilterRow>

          <Table<CompanyRow | EmployeeRow>
            columns={columns}
            dataSource={rows}
            pagination={false}
            loading={loading}
            scroll={{ x: 'max-content' }}
            size="small"
            bordered
            rowClassName={(record) =>
              (record as CompanyRow).isGrandTotal ? 'grand-total-row' : ''
            }
          />
        </>
      )}
    </SectionCard>
  )
}
